import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Download, Euro, Users, Wallet, FileText, Send } from "lucide-react";
import { eur, exportCSV } from "@/lib/format";

export function ReportTab() {
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/admin/report/test");
      toast.success(r.data.message || `Report di prova in invio a ${r.data.sent_to}. Controlla la casella tra poco.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio del report di prova non riuscito");
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    api.get("/admin/periods").then((r) => {
      setPeriods(r.data);
      const active = r.data.find((p) => p.is_active) || r.data[0];
      if (active) setSelected(active.id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/admin/periods/${selected}/report`).then((r) => setReport(r.data)).finally(() => setLoading(false));
  }, [selected]);

  const campaignTotal = report ? report.promotions.reduce((s, p) => s + p.totals.total_prize_value, 0) : 0;
  const managerTotal = report ? report.managers.reduce((s, m) => s + m.earning, 0) : 0;
  const totalCost = campaignTotal + managerTotal;

  const doPDF = () => {
    if (!report) return;
    const money = (v) => "€ " + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const promoHtml = report.promotions.map((p) => {
      const isPz = p.promotion.type === "pezzi";
      const unit = isPz ? "pezzo" : "vendita";
      const col = isPz ? "Pezzi" : "Vendite";
      return `
      <h3>${p.promotion.name} <small>(tipo: ${isPz ? "a pezzi" : "a vendite"} · soglia ${p.promotion.target} · ${money(p.promotion.value_per_sale)}/${unit})</small></h3>
      <table>
        <thead><tr><th>Agente</th><th>${col}</th><th>Stato</th><th class="r">Premio</th></tr></thead>
        <tbody>${p.entries.map((e) => `<tr><td>${e.agent_name}</td><td>${e.count}/${e.target}</td><td>${e.achieved ? "Sbloccato" : "Bloccato"}</td><td class="r">${money(e.value)}</td></tr>`).join("")}</tbody>
        <tfoot><tr><td colspan="3">Totale promozione</td><td class="r">${money(p.totals.total_prize_value)}</td></tr></tfoot>
      </table>`;
    }).join("");
    const mgrHtml = `
      <h3>Compensi capi area</h3>
      <table>
        <thead><tr><th>Capo Area</th><th>%</th><th>Premi team</th><th class="r">Compenso</th></tr></thead>
        <tbody>${report.managers.map((m) => `<tr><td>${m.full_name}</td><td>${m.commission_pct}%</td><td>${money(m.team_prize_value)}</td><td class="r">${money(m.earning)}</td></tr>`).join("")}</tbody>
      </table>`;
    const totalsHtml = `
      <div class="totals">
        <div><span>Totale premi campagna</span><strong>${money(campaignTotal)}</strong></div>
        <div><span>Compensi capi area</span><strong>${money(managerTotal)}</strong></div>
        <div class="grand"><span>Costo totale</span><strong>${money(totalCost)}</strong></div>
      </div>`;
    const style = `
      body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:32px;}
      h1{color:#0A5CB0;margin:0 0 4px;} .sub{color:#666;margin:0 0 24px;}
      h3{margin:24px 0 8px;} small{color:#888;font-weight:normal;}
      table{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:13px;}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;} th{background:#f2f8fc;}
      .r{text-align:right;} tfoot td{font-weight:bold;background:#fafafa;}
      .totals{margin-top:24px;border-top:2px solid #0A5CB0;padding-top:12px;max-width:360px;margin-left:auto;}
      .totals div{display:flex;justify-content:space-between;padding:4px 0;}
      .totals .grand{font-size:18px;color:#0A5CB0;border-top:1px solid #ddd;margin-top:6px;padding-top:8px;}
      @media print{body{padding:0;}}`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Abilita i popup per esportare il PDF"); return; }
    w.document.write(`<html><head><title>Report ${report.period.name}</title><style>${style}</style></head><body>
      <h1>Report Campagna — ${report.period.name}</h1>
      <p class="sub">Periodo ${report.period.start_date} – ${report.period.end_date}</p>
      ${promoHtml}${mgrHtml}${totalsHtml}
      <script>window.onload=function(){setTimeout(function(){window.print();},200);}<\/script>
    </body></html>`);
    w.document.close();
    toast.success("Report PDF pronto per la stampa");
  };

  const doExport = () => {
    if (!report) return;
    const rows = [];
    report.promotions.forEach((p) => {
      const tipo = p.promotion.type === "pezzi" ? "Pezzi" : "Vendite";
      p.entries.forEach((e) => {
        rows.push({
          Campagna: report.period.name, Promozione: p.promotion.name, Tipo: tipo, Agente: e.agent_name, Email: e.email,
          Conteggio: e.count, "Unità conteggio": tipo, Soglia: e.target, Sbloccato: e.achieved ? "Sì" : "No", "Premio €": e.value,
        });
      });
    });
    report.managers.forEach((m) => {
      rows.push({ Campagna: report.period.name, Promozione: "— COMPENSO CAPO AREA —", Tipo: "", Agente: m.full_name, Email: "",
        Conteggio: "", "Unità conteggio": "", Soglia: "", Sbloccato: `${m.commission_pct}%`, "Premio €": m.earning });
    });
    rows.push({ Campagna: report.period.name, Promozione: "TOTALE PREMI", Tipo: "", Agente: "", Email: "", Conteggio: "", "Unità conteggio": "", Soglia: "", Sbloccato: "", "Premio €": campaignTotal });
    rows.push({ Campagna: report.period.name, Promozione: "COMPENSI CAPI AREA", Tipo: "", Agente: "", Email: "", Conteggio: "", "Unità conteggio": "", Soglia: "", Sbloccato: "", "Premio €": managerTotal });
    rows.push({ Campagna: report.period.name, Promozione: "COSTO TOTALE", Tipo: "", Agente: "", Email: "", Conteggio: "", "Unità conteggio": "", Soglia: "", Sbloccato: "", "Premio €": totalCost });
    exportCSV(`report_${report.period.name.replace(/\s+/g, "_")}.csv`, rows);
    toast.success("CSV esportato con successo");
  };

  return (
    <div className="space-y-6" data-testid="report-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={selected || ""} onValueChange={setSelected}>
          <SelectTrigger className="w-64 bg-[#FFFFFF] border-[#DCE7F5]" data-testid="report-period-select"><SelectValue placeholder="Seleziona campagna" /></SelectTrigger>
          <SelectContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]">
            {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_active ? " (attiva)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button onClick={sendTest} disabled={sendingTest} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-white" data-testid="send-test-report-btn">
            <Send className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {sendingTest ? "Invio in corso…" : "Invia report di prova"}
          </Button>
          <Button variant="outline" onClick={doPDF} disabled={!report} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33]" data-testid="export-report-pdf-btn">
            <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Esporta PDF
          </Button>
          <Button variant="outline" onClick={doExport} disabled={!report} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33]" data-testid="export-report-csv-btn">
            <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Esporta CSV
          </Button>
        </div>
      </div>

      <p className="text-sm text-[#5A6B85] -mt-2" data-testid="test-report-hint">
        Il pulsante <strong>“Invia report di prova”</strong> invia <strong>solo a te</strong> una copia di <strong>ogni</strong> report che parte
        (quello globale + un report per ciascun Capo Area), con il <strong>PDF in allegato</strong> oltre al link, per verificare cosa riceve ogni destinatario. I destinatari reali non ricevono nulla.
      </p>

      {loading || !report ? <Skeleton className="h-64 bg-[#FFFFFF]" /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Summary icon={Euro} label="Totale premi campagna" value={eur(campaignTotal)} testid="report-campaign-total" />
            <Summary icon={Users} label="Compensi capi area" value={eur(managerTotal)} testid="report-manager-total" />
            <Summary icon={Wallet} label="Costo totale" value={eur(totalCost)} highlight testid="report-total-cost" />
          </div>

          {report.promotions.map((p) => (
            <Card key={p.promotion.id} className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
              <div className="p-4 border-b border-[#DCE7F5] flex items-center justify-between">
                <div className="font-display font-semibold flex items-center gap-2">
                  {p.promotion.name}
                  <Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5] text-[10px] uppercase tracking-wide" data-testid={`report-promo-type-${p.promotion.id}`}>{p.promotion.type === "pezzi" ? "A pezzi" : "A vendite"}</Badge>
                  <span className="text-xs text-[#5A6B85] font-normal">· soglia {p.promotion.target} · {eur(p.promotion.value_per_sale)}/{p.promotion.type === "pezzi" ? "pezzo" : "vendita"}</span>
                </div>
                <Badge className="bg-[#0B6BCB]/15 text-[#0B6BCB] border-[#0B6BCB]/30">{eur(p.totals.total_prize_value)}</Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                    <TableHead className="text-[#5A6B85]">Agente</TableHead>
                    <TableHead className="text-[#5A6B85] text-right">{p.promotion.type === "pezzi" ? "Pezzi" : "Vendite"}</TableHead>
                    <TableHead className="text-[#5A6B85] text-center">Stato</TableHead>
                    <TableHead className="text-[#5A6B85] text-right">Premio €</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {p.entries.map((e) => (
                    <TableRow key={e.agent_id} className="border-[#DCE7F5]">
                      <TableCell className="font-medium">{e.agent_name}</TableCell>
                      <TableCell className="text-right text-[#5A6B85]">{e.count} / {e.target}</TableCell>
                      <TableCell className="text-center">
                        <span className={e.achieved ? "text-[#0B6BCB] text-xs" : "text-[#94A3B8] text-xs"}>{e.achieved ? "Sbloccato" : "Bloccato"}</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold"><span className={e.value > 0 ? "text-[#0B6BCB]" : "text-[#94A3B8]"}>{eur(e.value)}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ))}

          <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
            <div className="p-4 border-b border-[#DCE7F5] font-display font-semibold">Compensi capi area</div>
            <Table>
              <TableHeader>
                <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                  <TableHead className="text-[#5A6B85]">Capo Area</TableHead>
                  <TableHead className="text-[#5A6B85] text-center">%</TableHead>
                  <TableHead className="text-[#5A6B85] text-right">Premi team €</TableHead>
                  <TableHead className="text-[#5A6B85] text-right">Compenso €</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.managers.map((m) => (
                  <TableRow key={m.manager_id} className="border-[#DCE7F5]">
                    <TableCell className="font-medium">{m.full_name}</TableCell>
                    <TableCell className="text-center text-[#5A6B85]">{m.commission_pct}%</TableCell>
                    <TableCell className="text-right text-[#5A6B85]">{eur(m.team_prize_value)}</TableCell>
                    <TableCell className="text-right font-semibold text-[#0B6BCB]">{eur(m.earning)}</TableCell>
                  </TableRow>
                ))}
                {report.managers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#5A6B85] py-6">Nessun capo area</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function Summary({ icon: Icon, label, value, highlight, testid }) {
  return (
    <Card className={`p-6 bg-[#FFFFFF] ${highlight ? "border-[#0B6BCB]/30" : "border-[#DCE7F5]"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[#5A6B85] mb-3"><Icon className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /><span className="text-xs uppercase tracking-wide">{label}</span></div>
      <div className={`font-display font-bold text-3xl tracking-tight ${highlight ? "text-[#0B6BCB]" : "text-[#0F1E33]"}`}>{value}</div>
    </Card>
  );
}
